/**
 * usePlanDates - Hook for centralized date and time management in meal plans
 * 
 * This hook provides a consistent API for all date-related operations in the
 * meal plan editor, including:
 * - Converting between dates and day offsets
 * - Formatting dates for display
 * - Calculating plan duration and date ranges
 * - Validating dates within plan boundaries
 * 
 * All dates are handled in local timezone to match existing behavior.
 * Storage format is YYYY-MM-DD (timezone-agnostic).
 */

import { useMemo, useCallback } from 'react';
import moment from 'moment';

/**
 * Hook for managing meal plan dates and offsets
 * 
 * @param {Object} config - Configuration object
 * @param {string} config.startDate - Plan start date in YYYY-MM-DD format
 * @param {Array} config.meals - Array of meals to calculate duration from
 * @param {number} config.defaultDuration - Default duration if no meals exist
 * @returns {Object} Date management utilities and computed values
 */
export function usePlanDates(config = {}) {
    const { 
        startDate: inputStartDate,
        meals = [],
        defaultDuration = 7
    } = config;

    // Use the provided start date directly
    const startDate = useMemo(() => {
        return inputStartDate || moment().format('YYYY-MM-DD');
    }, [inputStartDate]);

    // Calculate duration based on meals or use default
    const duration = useMemo(() => {
        if (meals.length === 0) {
            return defaultDuration;
        }
        
        // Find the maximum offset in meals
        const maxOffset = meals.reduce((max, meal) => {
            const offset = parseInt(meal.offset) || 0;
            return Math.max(max, offset);
        }, 0);
        
        // Duration is max offset + 1 (since offset is 0-based)
        return Math.max(maxOffset + 1, 1);
    }, [meals, defaultDuration]);


    /**
     * Convert a day offset to date
     * @param {number} offset - Day offset (0-based)
     * @returns {string} Date in YYYY-MM-DD format
     */
    const getDateFromOffset = useCallback((offset) => {
        return moment(startDate)
            .add(offset, 'days')
            .format('YYYY-MM-DD');
    }, [startDate]);

    /**
     * Format a date for display
     * @param {string|Date|moment} date - Date to format
     * @param {string} format - Moment format string (default: 'dddd MMMM Do')
     * @returns {string} Formatted date string
     */
    const formatDate = useCallback((date, format = 'dddd MMMM Do') => {
        return moment(date).format(format);
    }, []);


    /**
     * Get a moment instance for a given date or offset
     * For backward compatibility with components expecting moment objects
     * @param {string|Date|moment|number} dateOrOffset - Date or offset
     * @returns {moment} Moment instance
     */
    const getMoment = useCallback((dateOrOffset) => {
        if (typeof dateOrOffset === 'number') {
            // It's an offset
            return moment(getDateFromOffset(dateOrOffset));
        }
        return moment(dateOrOffset);
    }, [getDateFromOffset]);


    return {
        // Core dates
        startDate,
        duration,
        
        // Formatting
        formatDate,
        
        // Backward compatibility
        getMoment
    };
}

export default usePlanDates;